/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout from '../../../components/admin/InventoryLayout';
import UpdateStockModal from './components/UpdateStockModal';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    lowStockItems: [],
    totalProducts: 0
  });

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [lowStockResponse, itemsResponse] = await Promise.all([
        inventoryApi.alerts.getLowStock(),
        inventoryApi.items.getItems({ page: 1, pageSize: 1 })
      ]);

      setDashboardData({
        lowStockItems: lowStockResponse.items || [],
        totalProducts: itemsResponse.pagination?.total || (itemsResponse.items?.length || 0)
      });

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Notification helper
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Skeleton loader component
  const SkeletonCard = () => (
    <Card>
      <div className="p-4 md:p-5">
        <div className="animate-pulse">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-24 mb-2"></div>
              <div className="h-8 bg-zinc-300 dark:bg-zinc-600 rounded w-16"></div>
            </div>
            <div className="p-3 bg-zinc-200 dark:bg-zinc-700 rounded-full">
              <div className="w-6 h-6 bg-zinc-300 dark:bg-zinc-600 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <InventoryLayout 
      title="Inventory Dashboard" 
      subtitle="Manage your inventory and track stock levels"
      currentTab="dashboard"
    >
      {/* Mobile Header - Like Bookings Page */}
      <div className="md:hidden mb-6 pt-2">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Inventory Dashboard</h1>
            <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
              Manage your inventory and track stock levels
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {error && (
          <Card className="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20">
            <div className="p-4 md:p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Notification */}
        {notification && (
          <Card className={`${
            notification.type === 'success' 
              ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20'
              : notification.type === 'error'
              ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20'
              : 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20'
          }`}>
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                    notification.type === 'success' 
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : notification.type === 'error'
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : 'bg-blue-100 dark:bg-blue-900/30'
                  }`}>
                    <svg className={`h-5 w-5 ${
                      notification.type === 'success' 
                        ? 'text-green-600 dark:text-green-400'
                        : notification.type === 'error'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-blue-600 dark:text-blue-400'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {notification.type === 'success' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : notification.type === 'error' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                  </div>
                  <p className={`text-sm font-medium ${
                    notification.type === 'success' 
                      ? 'text-green-900 dark:text-green-100'
                      : notification.type === 'error'
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-blue-900 dark:text-blue-100'
                  }`}>{notification.message}</p>
                </div>
                <motion.button 
                  onClick={() => setNotification(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-lg font-bold hover:opacity-70 transition-opacity"
                >
                  ×
                </motion.button>
              </div>
            </div>
          </Card>
        )}

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {/* Low Stock Card */}
              <Card>
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Low Stock Items</p>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{dashboardData.lowStockItems.length}</p>
                    </div>
                    <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                      <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Total Products Card */}
              <Card>
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Total Products</p>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{dashboardData.totalProducts}</p>
                    </div>
                    <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                      <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Stock Health Card */}
              <Card>
                <div className="p-4 md:p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Stock Health</p>
                      <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                        {dashboardData.totalProducts > 0
                          ? `${Math.round(((dashboardData.totalProducts - dashboardData.lowStockItems.length) / dashboardData.totalProducts) * 100)}%`
                          : '—'}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                        {dashboardData.lowStockItems.length === 0 ? 'All stocked' : 'Needs attention'}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Low Stock Items Table */}
        <Card>
          <CardHeader
            title="Low Stock Items"
            subtitle="Items that need restocking"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            }
          />
          
          <div className="p-4 md:p-5">
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex space-x-4">
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/4"></div>
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/6"></div>
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/6"></div>
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/6"></div>
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/6"></div>
                    <div className="h-4 bg-zinc-300 dark:bg-zinc-600 rounded w-1/6"></div>
                  </div>
                ))}
              </div>
            ) : dashboardData.lowStockItems.length === 0 ? (
              <div className="text-center py-12">
                <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  You're fully stocked. Nice!
                </h3>
                <p className="text-zinc-500 dark:text-zinc-400">
                  All items are above their reorder points.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                      <th className="py-4 px-4 font-medium">Item</th>
                      <th className="py-4 px-4 font-medium">Location</th>
                      <th className="py-4 px-4 font-medium">On Hand</th>
                      <th className="py-4 px-4 font-medium">Reorder Point</th>
                      <th className="py-4 px-4 font-medium">Preferred Stock</th>
                      <th className="py-4 px-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 dark:divide-white/10">
                    {dashboardData.lowStockItems.map((item) => {
                      return (
                        <motion.tr 
                          key={`${item.itemId}-${item.locationId}`}
                          className="text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors"
                          whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                        >
                          <td className="py-4 px-4">
                            <div>
                              <div className="font-medium text-zinc-900 dark:text-zinc-100">{item.itemName}</div>
                              <div className="text-sm text-zinc-500 dark:text-zinc-400">{item.category}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">{item.locationName}</td>
                          <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">{item.onHand}</td>
                          <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">{item.reorderPoint}</td>
                          <td className="py-4 px-4 text-zinc-800 dark:text-zinc-100">{item.preferredStock}</td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center gap-2 justify-end">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                disabled={actionLoading}
                                onClick={async () => {
                                  try {
                                    setActionLoading(true);
                                    setSelectedItem({
                                      id: item.itemId,
                                      name: item.itemName,
                                      currentStock: item.onHand,
                                      locationId: item.locationId
                                    });
                                    setShowUpdateModal(true);
                                  } catch (e) {
                                    console.error('Failed to open update modal');
                                  } finally {
                                    setActionLoading(false);
                                  }
                                }}
                                className="px-3 py-2 text-xs rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-[--accent] hover:bg-[--accent]/10 transition-all"
                                style={{ '--accent': ACCENT }}
                              >
                                Update Stock
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      <UpdateStockModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={loadDashboardData}
        item={selectedItem}
      />
    </InventoryLayout>
  );
};

export default InventoryDashboard;
